import _ from 'lodash';

// A security group seeded from a TPO signup template carries a STRUCTURAL role name —
// `TPO-Seed.{subCompanyGuid:N}.{templateNameOrId}` (Skynet.Loans `TpoSeededRoleName`). The embedded
// sub-company id is the invariant that fences a broker's pipeline grants to their own sub-company, so
// the name cannot be made friendly server-side without breaking that fence — it is an identity key,
// not a label. These helpers recover the label for display only; anything SENT to the server (grant,
// revoke, delete) must keep using the raw name / roleId.
const SEEDED_ROLE_NAME_PATTERN = /^TPO-Seed\.[0-9a-f]{32}\.(.+)$/i;

// DEV-4697 made the template's id the preferred key, so the trailing segment is either the template's
// NAME (legacy, and every template in the field today) or its 32-hex id. An id is not resolvable to a
// name on the client, so we leave that name untouched rather than render a bare guid — a collapsed
// generic label would make distinct groups indistinguishable, which is worse.
// TODO(DEV-5677): drop this fallback once the allowed-roles DTO carries a `roleDisplayName`.
const TEMPLATE_ID_SEGMENT_PATTERN = /^[0-9a-f]{32}$/i;

// `isSeededFromTemplate` is the SERVER's answer to "is this a template group"; prefer it over the
// name shape wherever the caller has it. A broker can name a group they authored themselves, so a
// custom group called `TPO-Seed.<32-hex>.AFR Broker Loan Originator` would otherwise be stripped and
// render indistinguishably from the real seeded group on surfaces that carry no Custom badge (the
// Pipeline Access column headers). Callers with no flag to hand (the Team Role column reads a bare
// role name off `roleAssignments`) pass nothing and fall back to the shape.
export const getSecurityGroupDisplayName = (roleName, isSeededFromTemplate) => {
  if (isSeededFromTemplate === false) {
    return roleName;
  }

  const match = SEEDED_ROLE_NAME_PATTERN.exec(roleName || '');

  if (_.isNil(match)) {
    return roleName;
  }

  const [, templateSegment] = match;

  return TEMPLATE_ID_SEGMENT_PATTERN.test(templateSegment) ? roleName : templateSegment;
};

// A seeded group's label is a lender-authored template name — already human, and already correctly
// cased. Callers that prettify identifier-style role names (`loanOfficer` -> `Loan Officer`) must
// know not to apply that to it, or `AFR Broker Loan Originator` gets re-cased into something the
// lender never wrote.
export const checkIsSeededSecurityGroupName = roleName =>
  SEEDED_ROLE_NAME_PATTERN.test(roleName || '');
